// Core
import React, {
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Libraries
import ReactHlsPlayer from 'react-hls-player';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import _ from 'lodash';

// Plugins
import { timestampToTimeAgo } from '../../plugins/general';

// Layout
import { CoordinatesType, Map } from '../../layout/elements/Map';
import { ModalPanel } from '../../layout/elements/ModalPanel';
import {
  CloudOffLine,
  Expand,
  // Live, @TODO: uncomment this and apply the camera logic after first release
  MapOutline,
  MapPinFill,
} from '../../layout/icons';
import {
  Paragraph,
  ParagraphWeight,
  ParagraphAlign,
  ParagraphSize,
} from '../../layout/elements/Paragraph';
import { Tooltip, TooltipPlacement } from '../../layout/elements/Tooltip';

// Component
import {
  // CamButton, // @TODO: uncomment this and apply the camera logic after first release
  ActionsContainer,
  CameraContainer,
  CameraFeedContainer,
  CameraModalContainer,
  CameraOffOverlay,
  CameraOnOverlay,
  CloudOff,
  ContainerTitle,
  CrusherSimulationImage,
  FullScreenButton,
  LastSeenTime,
  LeftActionsContainer,
  LiveContainer,
  LiveIndicator,
  MapButton,
  MapLastSeenContainer,
  MapViewContainer,
  MapViewModalContainer,
  OfflineMessageContainer,
  SwitchContainer,
} from './CameraFeed.style';
import { CameraFeedProps, LiveStatus, ViewType } from './CameraFeed.types';

// Types
import { Theme } from '../../types/theme';

// Media
import crusherSimulation from '../../media/crusherSimulation.gif';

// Constants
const GPS_CONNECTION_THRESHOLD: number = 1000 * 60 * 60 * 12; // 12 hours
const STABLE_LOCATION_DISTANCE_THRESHOLD_METERS: number = 50; // 50 meters

function CameraFeed({
  className,
  gpsEnabled,
  isOpen = false,
  live = LiveStatus.off,
  onOpen,
  polygons,
  src,
  testId,
  title,
  unitCoordinates,
}: CameraFeedProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();

  /* **********************************************************************************************
  ***************************************** LOCAL STATES ******************************************
  ********************************************************************************************** */

  /* Initial state */
  const initialState: {
    currentView: ViewType,
    stabledLocation: CoordinatesType | null,
  } = {
    currentView: ViewType.map, // @TODO: switch to camera after first release
    stabledLocation: null,
  };

  const [state, setState] = useState(initialState);

  /* ***********************************************************************************************
  ******************************************* METHODS **********************************************
  *********************************************************************************************** */

  const playerRef: RefObject<HTMLVideoElement> = useRef(null);
  const containerRef: RefObject<HTMLDivElement> = useRef(null);

  // Function that activates autoplay when the status is on
  const autoPlay = (liveStatus: CameraFeedProps['live']): boolean => liveStatus === LiveStatus.on;

  // Error message for unauthorized [src]
  if (src && !src.endsWith('.m3u8')) {
    throw new Error('Error: [src] invalid url, expected .m3u8');
  }

  // Function that toggles between camera and map view
  // const handleToggleView = (): void => {
  //   setState((prevState) => ({
  //     ...prevState,
  //     currentView: prevState.currentView === ViewType.camera ? ViewType.map : ViewType.camera,
  //   }));
  // };

  const actionButtons = ({ isModalOpen }: { isModalOpen: boolean }): JSX.Element => (
    <ActionsContainer>
      <SwitchContainer isModalOpen={isModalOpen}>
        {/* @TODO: uncomment this and apply the camera logic after first release */}
        {/* <CamButton currentView={state.currentView} onClick={handleToggleView}> */}
        {/*  <Live /> */}
        {/* </CamButton> */}
        <MapButton currentView={state.currentView} onClick={() => {}}>
          <MapOutline />
        </MapButton>
      </SwitchContainer>
      {!isModalOpen && (
        <FullScreenButton onClick={(): void => {
          if (onOpen) {
            onOpen(true);
          }
        }}
        >
          <Expand />
        </FullScreenButton>
      )}
    </ActionsContainer>
  );

  const cameraOn = (): JSX.Element => (
    <>
      <CameraOnOverlay />
      <LiveContainer>
        <LiveIndicator />
        <Paragraph
          weight={ParagraphWeight.medium}
          testId="live-text"
          color={theme.color.cameraFeed.textColor}
        >
          {t('common.live')}
        </Paragraph>
      </LiveContainer>
      {src && src.endsWith('.m3u8')
        ? (
          <ReactHlsPlayer
            id="react-hls-player"
            src={`${src}`}
            playerRef={playerRef}
            autoPlay={autoPlay(live)}
            muted
          />
        ) : (
          <CrusherSimulationImage src={crusherSimulation} alt="Crusher Simulation" />
        )}
    </>
  );

  const cameraOff = (): JSX.Element => (
    <>
      <CameraOffOverlay />
      <OfflineMessageContainer>
        <CloudOff>
          <CloudOffLine />
        </CloudOff>
        <Paragraph
          weight={ParagraphWeight.medium}
          size={ParagraphSize.xl}
          color={theme.color.cameraFeed.textColor}
        >
          {t('layout.elements.cameraFeed.cameraOffline')}
        </Paragraph>
        <Paragraph
          align={ParagraphAlign.center}
          color={theme.color.cameraFeed.textColor}
        >
          {t('layout.elements.cameraFeed.cameraOffDescription')}
        </Paragraph>
      </OfflineMessageContainer>
    </>
  );

  const isGPSConnected: boolean = useMemo(() => {
    if (!_.isNil(unitCoordinates?.gpsTimestamp)) {
      return (Date.now() - (unitCoordinates?.gpsTimestamp as number)) <= GPS_CONNECTION_THRESHOLD;
    }
    return false;
  }, [unitCoordinates]);

  const getHaversineDistance = (
    coords1: CoordinatesType,
    coords2: CoordinatesType,
  ): number => {
    if (!coords1 || !coords2) return 0;

    const toRad = (value: number): number => (value * Math.PI) / 180;
    const R: number = 6371e3;

    const dLat: number = toRad(coords2.lat - coords1.lat);
    const dLng: number = toRad(coords2.lng - coords1.lng);
    const lat1: number = toRad(coords1.lat);
    const lat2: number = toRad(coords2.lat);

    const a: number = Math.sin(dLat / 2) * Math.sin(dLat / 2)
      + Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c: number = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  useEffect(() => {
    if (!_.isNil(unitCoordinates?.lat) && !_.isNil(unitCoordinates?.lng)) {
      const newCoords: CoordinatesType = {
        lat: unitCoordinates?.lat as number,
        lng: unitCoordinates?.lng as number,
      };

      setState((prevState) => {
        if (!prevState.stabledLocation) {
          return { ...prevState, stabledLocation: newCoords };
        }

        const distance: number = getHaversineDistance(
          prevState.stabledLocation,
          newCoords,
        );

        if (distance > STABLE_LOCATION_DISTANCE_THRESHOLD_METERS) {
          return { ...prevState, stabledLocation: newCoords };
        }

        return prevState;
      });
    }
  }, [unitCoordinates]);

  return (
    <CameraFeedContainer
      data-testid={testId}
      className={className}
      ref={containerRef}
    >
      <ContainerTitle>
        <Paragraph
          weight={ParagraphWeight.medium}
          size={ParagraphSize.sm}
          color={theme.color.cameraFeed.textColor}
        >
          {title}
        </Paragraph>
      </ContainerTitle>
      {actionButtons({ isModalOpen: false })}
      {gpsEnabled && (
        <LeftActionsContainer isConnected={isGPSConnected} isModalOpen={false}>
          <Tooltip
            text={(
              <Trans
                i18nKey="common.gpsLastSeen"
                values={{
                  lastSeen: !_.isNil(unitCoordinates?.gpsTimestamp)
                    ? timestampToTimeAgo(unitCoordinates?.gpsTimestamp)
                    : t('common.neverSeen'),
                }}
                components={{ lastSeenTime: <LastSeenTime /> }}
              />
            )}
          >
            <MapLastSeenContainer>
              <MapPinFill />
            </MapLastSeenContainer>
          </Tooltip>
        </LeftActionsContainer>
      )}
      {state.currentView === ViewType.map
        ? (
          <MapViewContainer>
            <Map
              pins={!_.isNil(state.stabledLocation) ? [{
                lat: state.stabledLocation?.lat as number,
                lng: state.stabledLocation?.lng as number,
              }] : []}
              polygons={polygons}
              centerCoordinates={
                !_.isNil(state.stabledLocation)
                  ? state.stabledLocation
                  : undefined
              }
            />
          </MapViewContainer>
        ) : (
          <CameraContainer>
            {
              state.currentView === ViewType.camera
              && live === LiveStatus.on ? cameraOn() : cameraOff()
            }
          </CameraContainer>
        )}
      <ModalPanel
        onClose={(): void => {
          if (onOpen) {
            onOpen(false);
          }
        }}
        showModal={isOpen}
        title={title}
      >
        {state.currentView === ViewType.map
          ? (
            <MapViewModalContainer>
              {actionButtons({ isModalOpen: true })}
              {gpsEnabled && (
                <LeftActionsContainer isConnected={isGPSConnected} isModalOpen>
                  <Tooltip
                    text={(
                      <Trans
                        i18nKey="common.gpsLastSeen"
                        values={{
                          lastSeen: !_.isNil(unitCoordinates?.gpsTimestamp)
                            ? timestampToTimeAgo(unitCoordinates?.gpsTimestamp)
                            : t('common.neverSeen'),
                        }}
                        components={{ lastSeenTime: <LastSeenTime /> }}
                      />
                    )}
                    placement={TooltipPlacement.right}
                  >
                    <MapLastSeenContainer>
                      <MapPinFill />
                    </MapLastSeenContainer>
                  </Tooltip>
                </LeftActionsContainer>
              )}
              <ContainerTitle>
                <Paragraph
                  weight={ParagraphWeight.medium}
                  size={ParagraphSize.sm}
                  color={theme.color.cameraFeed.textColor}
                >
                  {title}
                </Paragraph>
              </ContainerTitle>
              <Map
                pins={[{
                  lat: unitCoordinates?.lat as number,
                  lng: unitCoordinates?.lng as number,
                }]}
                polygons={polygons}
                centerCoordinates={
                  !_.isNil(state.stabledLocation)
                    ? state.stabledLocation
                    : undefined
                }
              />
            </MapViewModalContainer>
          ) : (
            <CameraModalContainer>
              {actionButtons({ isModalOpen: true })}
              <ContainerTitle>
                <Paragraph
                  weight={ParagraphWeight.medium}
                  size={ParagraphSize.sm}
                  color={theme.color.cameraFeed.textColor}
                >
                  {title}
                </Paragraph>
              </ContainerTitle>
              {
                state.currentView === ViewType.camera
                && live === LiveStatus.on ? cameraOn() : cameraOff()
              }
            </CameraModalContainer>
          )}
      </ModalPanel>
    </CameraFeedContainer>
  );
}

export { CameraFeed };
